import { useMutation } from "@tanstack/react-query";
import { chatCreateSession, chatStreamMessage, streamAgent, type ToolConfirmationDecision } from "@/api";
import { type CloudDoc, cloudMessageFields } from "./cloudFiles";

/**
 * What this client renders (MHS-795, MHS-794, MHS-971): proposal cards, so the backend offers tools that need the
 * user's confirmation (research), approval cards for write actions (send an email, post to Slack...), and `[n]`
 * citation markers, so answers cite their sources by number.
 */
const CLIENT_FEATURES = ["tool_confirmation", "action_approval", "citations"];

export interface SendInput {
  content: string;
  /** Existing chat; null starts a new one. */
  sessionUuid: string | null;
  /** The agent answering (new chats: the picked assistant; open chats: the session's agent). */
  agentUuid: string | null;
  /** For a new plain chat: a model id or `tier:<name>`. */
  modelId: string | null;
  /** For a new chat: the picked thinking level (null = provider default). */
  reasoningEffort: string | null;
  /** For a new chat: start it in this space. */
  spaceUuid?: string | null;
  /** For a new chat: its brand (null/omitted = the company default). */
  brandUuid?: string | null;
  /** For a new chat: a Build with AI chat (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §3). */
  source?: "build";
  imageAttachments?: string[];
  documentAttachments?: string[];
  /** SharePoint/OneDrive/Drive files, content inline (see cloudFiles.ts). */
  cloudDocs?: CloudDoc[];
  /** Continue a reply that paused at the step limit (MHS-860): the backend resumes the work. */
  continuation?: boolean;
  /** The user's decision on a proposal card in this chat; `content` is the button's label. */
  confirmation?: ToolConfirmationDecision;
  /** Try again: the operation of the chat's latest reply, which this answer replaces (`content` is its question). */
  retryOf?: string;
}

export interface SendResult {
  sessionUuid: string;
  opId: string;
}

/**
 * Sends a message and starts the streamed reply. Agent chats go through the
 * agent stream endpoint (which creates the session on first send); plain
 * chats create the session first, then stream.
 */
const nonEmpty = (ids?: string[]) => (ids && ids.length > 0 ? ids : undefined);

async function createSession(input: SendInput): Promise<string> {
  if (!input.modelId) throw new Error("No model available");
  const { data } = await chatCreateSession({
    body: {
      modelId: input.modelId,
      reasoningEffort: input.reasoningEffort,
      ...(input.agentUuid ? { agentUuid: input.agentUuid } : {}),
      ...(input.spaceUuid ? { spaceUuid: input.spaceUuid } : {}),
      ...(input.brandUuid ? { brandUuid: input.brandUuid } : {}),
      ...(input.source ? { source: input.source } : {}),
    },
    throwOnError: true,
  });
  return data.data.uuid;
}

export function useSendMessage() {
  return useMutation({
    mutationFn: async (input: SendInput): Promise<SendResult> => {
      // The agent stream can't place a new chat in a space or give it a brand, so create it first and send into it.
      if ((input.spaceUuid || input.brandUuid) && !input.sessionUuid) {
        input = { ...input, sessionUuid: await createSession(input) };
      }
      if (input.agentUuid) {
        const { data } = await streamAgent({
          path: { agent_uuid: input.agentUuid },
          body: {
            message: input.content,
            sessionUuid: input.sessionUuid,
            ...(input.continuation ? { continuation: true } : {}),
            reasoningEffort: input.sessionUuid ? undefined : input.reasoningEffort,
            imageAttachments: nonEmpty(input.imageAttachments),
            documentAttachments: nonEmpty(input.documentAttachments),
            ...cloudMessageFields(input.cloudDocs ?? []),
            clientFeatures: CLIENT_FEATURES,
            timeZone: browserTimeZone(),
            confirmation: input.confirmation,
            retryOf: input.retryOf,
          },
          throwOnError: true,
        });
        return { sessionUuid: data.session_uuid ?? input.sessionUuid ?? "", opId: data.operation_id };
      }
      let sessionUuid = input.sessionUuid;
      if (!sessionUuid) {
        sessionUuid = await createSession(input);
      }
      const { data } = await chatStreamMessage({
        path: { session_uuid: sessionUuid },
        body: {
          content: input.content,
          ...(input.continuation ? { continuation: true } : {}),
          imageAttachments: nonEmpty(input.imageAttachments),
          documentAttachments: nonEmpty(input.documentAttachments),
          ...cloudMessageFields(input.cloudDocs ?? []),
          // This client renders draft cards, so the backend offers the Build with AI skills and tools.
          draftCards: true,
          clientFeatures: CLIENT_FEATURES,
          timeZone: browserTimeZone(),
          confirmation: input.confirmation,
          retryOf: input.retryOf,
        },
        throwOnError: true,
      });
      return { sessionUuid, opId: data.operation_id };
    },
  });
}

/** The browser's time zone, kept on the user so dates, times and meetings are theirs (UTC, the default, isn't sent). */
function browserTimeZone(): string | undefined {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zone && zone !== "UTC" ? zone : undefined;
}
